import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { getHighlightStyle } from "../../bible/highlightStorage";
import { SELECTION_DOCK_RESERVE_PX } from "../../theme/readerLayout";

export const processLine = (line) => {
  const match = line.trim().match(/^(\d+)\s*(.+)?$/);
  if (match) {
    return {
      number: match[1],
      text: match[2]?.trim() || "",
    };
  }
  return {
    number: null,
    text: line.trim(),
  };
};

export const parseChapterLines = (lines = []) => {
  const parsed = lines.map((line, index) => {
    const { number, text } = processLine(line);
    const verseNumber = number ? Number(number) : null;
    return {
      index,
      line,
      number,
      verseNumber: Number.isFinite(verseNumber) ? verseNumber : null,
      text,
      prevVerseNumber: null,
      nextVerseNumber: null,
    };
  });

  let lastVerse = null;
  for (let i = 0; i < parsed.length; i += 1) {
    parsed[i].prevVerseNumber = lastVerse;
    if (parsed[i].verseNumber !== null) {
      lastVerse = parsed[i].verseNumber;
    }
  }

  let upcomingVerse = null;
  for (let i = parsed.length - 1; i >= 0; i -= 1) {
    parsed[i].nextVerseNumber = upcomingVerse;
    if (parsed[i].verseNumber !== null) {
      upcomingVerse = parsed[i].verseNumber;
    }
  }

  return parsed;
};

const VerseLine = ({
  item,
  headingList = [],
  selected,
  prevSelected,
  nextSelected,
  isStart,
  isEnd,
  speaking,
  hasSelection,
  hlColor,
  matchingNote,
  verseNotes = [],
  verseCommentsEnabled = true,
  isZenMode = false,
  isDark,
  isMobile,
  fontSize,
  lineHeight = 1.8,
  fontFamily = "inherit",
  textColor,
  ink,
  wash,
  listenInk,
  listenWash,
  edition,
  onVerseClick,
  onListenFromVerse,
  onOpenVerseNote,
  onOpenVerseComments,
  onCopyTextOnly,
  onCopyEnhanced,
  onShareWhatsApp,
}) => {
  const { index, number, verseNumber, text } = item;
  const hlStyle = hlColor ? getHighlightStyle(hlColor, isDark) : null;

  return (
    <Box>
      {headingList.map((heading, headingIndex) => (
        <Box
          key={`${index}-heading-${headingIndex}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 4,
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              flex: 1,
              fontWeight: "bold",
              color: "primary.dark",
              borderLeft: "4px solid",
              borderColor: "primary.main",
              pl: 2,
              py: 0.5,
              letterSpacing: "0.05em",
            }}
          >
            {heading}
          </Typography>
          <Tooltip title={edition.ui.listenFromHere}>
            <IconButton
              size="small"
              aria-label={edition.ui.listenFromHere}
              onClick={(event) => {
                event.stopPropagation();
                onListenFromVerse(verseNumber);
              }}
            >
              <PlayArrowIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
      <Typography
        paragraph
        data-verse={number || undefined}
        aria-selected={selected}
        onClick={
          number ? (event) => onVerseClick(event, number) : undefined
        }
        sx={{
          display: "block",
          lineHeight,
          fontFamily,
          mb: selected && nextSelected ? 0 : 1.5,
          mt: selected && prevSelected ? 0 : undefined,
          p: 0.75,
          pl: 1.25,
          scrollMarginBottom: {
            xs: `${SELECTION_DOCK_RESERVE_PX.xs}px`,
            sm: `${SELECTION_DOCK_RESERVE_PX.sm}px`,
          },
          cursor: number ? "pointer" : "default",
          userSelect: isMobile ? "none" : "text",
          WebkitUserSelect: isMobile ? "none" : "text",
          touchAction: "manipulation",
          bgcolor: speaking
            ? listenWash
            : selected
              ? hlStyle
                ? hlStyle.backgroundColor
                : wash
              : hlStyle
                ? hlStyle.backgroundColor
                : "transparent",
          boxShadow: speaking
            ? `inset 3px 0 0 ${listenInk}`
            : selected
              ? hlStyle
                ? `inset 4px 0 0 ${hlStyle.borderLeft ? hlStyle.borderLeft.split(" ")[2] : ink}`
                : `inset 3px 0 0 ${ink}`
              : hlStyle
                ? hlStyle.borderLeft
                  ? `inset 3px 0 0 ${hlStyle.borderLeft.split(" ")[2]}`
                  : "none"
                : "none",
          borderRadius: selected
            ? `${isStart || !prevSelected ? 10 : 0}px ${
                isStart || !prevSelected ? 10 : 0
              }px ${isEnd || !nextSelected ? 10 : 0}px ${
                isEnd || !nextSelected ? 10 : 0
              }px`
            : 1,
          transition:
            "background-color 160ms ease, box-shadow 160ms ease, border-radius 160ms ease",
          "&:hover": {
            bgcolor: speaking
              ? listenWash
              : selected
                ? wash
                : hlStyle
                  ? hlStyle.backgroundColor
                  : "action.hover",
            "& .verse-actions-inline": {
              opacity: hasSelection ? 0 : 1,
            },
          },
        }}
      >
        {number && (
          <Box
            component="span"
            sx={{
              color: speaking
                ? listenInk
                : selected
                  ? ink
                  : "primary.main",
              fontWeight: "bold",
              fontSize: `${fontSize}px`,
              fontFamily: "inherit",
              mr: 1,
              userSelect: "none",
              position: "relative",
              "&::before": isStart
                ? {
                    content: '""',
                    display: "inline-block",
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: ink,
                    mr: 0.75,
                    mb: "0.15em",
                    verticalAlign: "middle",
                  }
                : undefined,
            }}
          >
            {number}
          </Box>
        )}
        {matchingNote && (
          <Tooltip
            title={`灵修笔记：${matchingNote.title || "点击查看"}`}
            placement="top"
          >
            <Box
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                onOpenVerseNote(number, text, matchingNote);
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
                mr: 0.75,
                verticalAlign: "middle",
                color: "secondary.main",
                "&:hover": { transform: "scale(1.25)" },
                transition: "transform 0.15s ease",
              }}
            >
              <NoteAltIcon sx={{ fontSize: 16 }} />
            </Box>
          </Tooltip>
        )}
        <Box
          component="span"
          sx={{
            fontSize: `${fontSize}px`,
            fontFamily,
            color: textColor,
            verticalAlign: "baseline",
          }}
        >
          {text}
        </Box>

        {verseCommentsEnabled && !isZenMode && verseNotes.length > 0 && (
          <Tooltip
            title={`查看本节 ${verseNotes.length} 条节评`}
            placement="top"
          >
            <Box
              component="span"
              role="button"
              tabIndex={0}
              data-testid={`verse-comment-bubble-${number}`}
              aria-label={`查看第 ${number} 节的 ${verseNotes.length} 条节评`}
              onClick={(e) => {
                e.stopPropagation();
                if (onOpenVerseComments) {
                  onOpenVerseComments(verseNumber, text);
                } else if (onOpenVerseNote) {
                  onOpenVerseNote(number, text, verseNotes[0]);
                }
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.35,
                ml: 0.75,
                px: 0.75,
                py: 0.1,
                borderRadius: 999,
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "primary.main",
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(226, 196, 138, 0.14)"
                    : "rgba(196, 162, 101, 0.16)",
                border: "1px solid",
                borderColor: "primary.main",
                verticalAlign: "middle",
                userSelect: "none",
                cursor: "pointer",
                transition: "transform 150ms ease",
                "&:hover": {
                  transform: "scale(1.08)",
                },
              }}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: 12 }} />
              {verseNotes.length}
            </Box>
          </Tooltip>
        )}

        {!isMobile && (
          <Box
            component="span"
            className="verse-actions-inline"
            sx={{
              display: hasSelection ? "none" : "inline-flex",
              ml: 2,
              gap: 0.5,
              verticalAlign: "middle",
              opacity: 0,
              transition: "opacity 0.2s ease",
              userSelect: "none",
            }}
          >
            <Tooltip title={edition.ui.copyText} placement="top" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onCopyTextOnly(text);
                }}
                sx={{ p: 0.5 }}
              >
                <ContentCopyIcon
                  fontSize="inherit"
                  sx={{ fontSize: "1.2rem" }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title={edition.ui.copyWithRef} placement="top" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onCopyEnhanced(number, text);
                }}
                sx={{ p: 0.5 }}
              >
                <LibraryAddCheckIcon
                  fontSize="inherit"
                  sx={{ fontSize: "1.2rem" }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title={edition.ui.shareWhatsApp} placement="top" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onShareWhatsApp(number, text);
                }}
                sx={{ p: 0.5 }}
              >
                <WhatsAppIcon
                  fontSize="inherit"
                  sx={{ fontSize: "1.2rem", color: "#25D366" }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={matchingNote ? "查看/编辑笔记" : "写笔记"}
              placement="top"
              arrow
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenVerseNote(number, text, matchingNote);
                }}
                sx={{
                  p: 0.5,
                  color: matchingNote ? "secondary.main" : "inherit",
                }}
              >
                <NoteAltIcon
                  fontSize="inherit"
                  sx={{ fontSize: "1.2rem" }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Typography>
    </Box>
  );
};

export default VerseLine;
